import { useState } from 'react';
import type { CareRequest } from '../hooks/useCareRequests';
import StatusBadge from './StatusBadge';
import KakaoLinkSender from './KakaoLinkSender';

interface Props {
  row: CareRequest;
  onClose: () => void;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || <span className="text-gray-300">-</span>}</p>
    </div>
  );
}

export default function DetailModal({ row, onClose }: Props) {
  const [showSsn, setShowSsn] = useState(false);

  const maskedSsn = row.caregiver_ssn
    ? row.caregiver_ssn.replace(/^(\d{6})-(\d{7})$/, '$1-*******')
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#e8f5f1' }}>
              <i className="ri-file-list-3-line text-sm" style={{ color: '#2E7D6B' }}></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">접수번호</p>
              <p className="text-sm font-bold font-mono text-gray-900">{row.receipt_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {row.is_duplicate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600 whitespace-nowrap">
                <i className="ri-error-warning-line"></i>중복
              </span>
            )}
            <StatusBadge status={row.status} />
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* 기본 */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-xl">
            <Field label="접수시간" value={formatDate(row.receipt_time)} />
            <Field label="간병인 등록번호" value={row.caregiver_reg_number} />
          </div>

          {/* 간병인 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-md" style={{ backgroundColor: '#e8f5f1' }}>
                <i className="ri-nurse-line text-xs" style={{ color: '#2E7D6B' }}></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">간병인 정보</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="성명" value={row.caregiver_name} />
              <Field label="연락처" value={row.caregiver_phone} />
              <Field label="생년월일" value={row.caregiver_birth} />
              <Field label="성별" value={row.caregiver_gender} />
              <div className="col-span-2">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">주민등록번호</p>
                  {row.caregiver_ssn ? (
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-800 tracking-widest font-mono">
                        {showSsn ? row.caregiver_ssn : maskedSsn}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowSsn((v) => !v)}
                        className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-md cursor-pointer transition-colors whitespace-nowrap"
                        style={{ backgroundColor: showSsn ? '#fef2f2' : '#f0f9f6', color: showSsn ? '#dc2626' : '#2E7D6B' }}
                      >
                        <i className={showSsn ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                        {showSsn ? '숨기기' : '확인'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-300">-</p>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <Field label="주소" value={row.caregiver_address} />
              </div>
            </div>
          </div>

          {/* 환자 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-md" style={{ backgroundColor: '#fff4e8' }}>
                <i className="ri-heart-pulse-line text-xs" style={{ color: '#e07b39' }}></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">환자 정보</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="성명" value={row.patient_name} />
              <Field label="연락처" value={row.patient_phone} />
              <Field label="생년월일" value={row.patient_birth} />
              <Field label="성별" value={row.patient_gender} />
              <div className="col-span-2">
                <Field label="주소" value={row.patient_address} />
              </div>
            </div>
          </div>

          {/* 보험 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-md" style={{ backgroundColor: '#eef2ff' }}>
                <i className="ri-shield-check-line text-xs" style={{ color: '#5c6bc0' }}></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">보험 정보</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="가입보험사" value={row.insurance_company} />
              <Field label="설계사 연락처" value={row.agent_phone} />
            </div>
          </div>

          {/* 카카오톡 링크 문자 발송 */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFDE7' }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-md" style={{ backgroundColor: '#FEE500' }}>
                <i className="ri-kakao-talk-fill text-xs" style={{ color: '#391B1B' }}></i>
              </div>
              <h4 className="text-sm font-bold text-gray-900">카카오톡 상담 안내</h4>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              신청자에게 카카오톡 상담 링크를 문자로 전송할 수 있습니다.
            </p>
            <div className="flex flex-wrap gap-3">
              <KakaoLinkSender phone={row.caregiver_phone} name={row.caregiver_name} />
              <KakaoLinkSender phone={row.patient_phone} name={row.patient_name} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
